import { AgePricingRangeForm } from '@/components/features/offerings/AgePricingRangeForm'
import { serviceTypeBadge } from '@/components/features/offerings/common'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { CreateAgreementData } from '@/domain/agreements/agreement'
import { AssuredPersonRequest, formatAssuredPersonRelationship } from '@/domain/assured/Assured'
import { ClientDetail } from '@/domain/clients/client'
import { Collector, getCollectorFullName } from '@/domain/collectors/collector'
import { DeathData, formatDeathRelationship } from '@/domain/deathData/deathData'
import { formatBillingFrequencyOnLabel, formatBillingFrequencyShort, Offering, OfferingAgePricingRange, ServiceType } from '@/domain/offerings/offering'
import { formatSellCode } from '@/domain/parcels/parcel'
import { calculateAgeStr } from '@/utils/dates'
import { convertToDigitStr } from '@/utils/helpers'
import { addMonths, differenceInYears, format } from 'date-fns'
import { es } from 'date-fns/locale'
import { FileText, Info, Users } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { PriceLabel, renderSinglePeriod } from '../../shared/utils'

interface AgreementSummaryProps {
  agreement: CreateAgreementData
  client?: ClientDetail
  seller?: Collector
  collector?: Collector
  offering?: Offering
  deathData?: DeathData[]
  assuredPeople?: AssuredPersonRequest[]
  ranges?: OfferingAgePricingRange[]
  form?: ReturnType<typeof useForm<CreateAgreementData>>
}

export const AgreementSummary = ({
  agreement,
  client,
  seller,
  collector,
  offering,
  deathData,
  assuredPeople,
  ranges,
  form,
}: AgreementSummaryProps) => {
  const [isAgePricingModalOpen, setIsAgePricingModalOpen] = useState(false)

  const oldestAge = assuredPeople?.reduce((max, person) => {
    if (!person.dateOfBirth) return max
    const age = differenceInYears(new Date(), person.dateOfBirth)
    return Math.max(max, age)
  }, 0) || 0

  const applicableRange = ranges?.find(
    range => oldestAge >= range.rangeStart && oldestAge <= range.rangeEnd
  )

  const requiresAdvancePayment = offering?.hasPaymentPlanAvailable && agreement.serviceType === ServiceType.ABONOS
  // console.log(agreement)
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Resumen de la contratación
          </CardTitle>
          <CardDescription>
            Revise los detalles antes de confirmar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Servicio</label>
              <p className="font-medium">{offering?.name || 'No seleccionado'}</p>
              <p className="font-mono text-sm text-muted-foreground">Código: {offering?.code || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tipo de servicio</label>
              <div className="font-medium">{serviceTypeBadge(agreement.serviceType!)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Cliente</label>
              <p className="font-medium">{client?.userData.code || ''} - {client?.userData.legalName || 'No seleccionado'}</p>
            </div>
            {agreement.sellCode && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tipo de Venta</label>
                <p className="font-medium">{formatSellCode(agreement.sellCode)}</p>
              </div>
            )}
            {seller && (
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  {/* <User className="h-4 w-4" /> */}
                  Vendedor
                </label>
                <p className="font-medium">{getCollectorFullName(seller)}</p>
                {/* <p className="font-mono text-sm text-muted-foreground">Código: </p> */}
              </div>
            )}
            {collector && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Cobrador</label>
                <p className="font-medium">{getCollectorFullName(collector)}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Fecha de inicio del contrato</label>
              <p className="font-medium">
                {agreement.startDate ? format(new Date(agreement.startDate), 'PPP', { locale: es }) : '-'}
              </p>
            </div>
            {agreement.contractNumber && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Número de Contrato</label>
                <p className="font-medium">{agreement.contractNumber}</p>
              </div>
            )}
            {agreement.endDate && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de cremación</label>
                <p className="font-medium">
                  {format(new Date(agreement.endDate), 'PPP', { locale: es })}
                </p>
              </div>
            )}
            {agreement.serviceType === ServiceType.ABONOS && <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Período de inicio del abono</label>
              <div className="flex flex-row items-center justify-start gap-5 mt-1">
                <span className={`capitalize font-medium`}>
                  {requiresAdvancePayment ? renderSinglePeriod(addMonths(agreement.startDate, 1), '/') : renderSinglePeriod(agreement.startDate, '/')}
                </span>
              </div>
            </div>}
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Monto final {formatBillingFrequencyOnLabel(offering!.billingFrequency)}
              </label>
              <div className="flex items-center gap-2">
                <PriceLabel price={agreement.amount!} />
                <span className='font-mono'>{formatBillingFrequencyShort(offering!.billingFrequency)}</span>
              </div>
            </div>
            {agreement.hasPaymentPlan && (
              <>
                {agreement.initialAmount && agreement.initialAmount > 0 ? (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Monto de Entrega Inicial
                    </label>
                    <div className="flex items-center gap-2">
                      <PriceLabel price={agreement.initialAmount || 0} />
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {agreement.serviceType === ServiceType.ABONOS ? 'Monto de Anticipo' : 'Monto de financiación'}
                  </label>
                  <div className="flex items-center gap-2">
                    <PriceLabel price={agreement.installmentAmount!} />
                    {agreement.installments && agreement.installmentAmount && agreement.installments > 1 && (
                      <span className="text-sm text-muted-foreground">en {agreement.installments} cuotas de ${convertToDigitStr(agreement.installmentAmount / agreement.installments)}</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Primer pago a cobrar
                  </label>
                  <div className="flex items-center gap-2">
                    <PriceLabel price={agreement.initialAmount && agreement.initialAmount > 0 ? agreement.initialAmount : (agreement.installmentAmount! / agreement.installments!)} />
                  </div>
                </div>
              </>
            )}
            {applicableRange && (
              <div className="col-span-1 sm:col-span-2">
                <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-800 p-3 rounded-md border border-blue-100">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1">
                    El precio se determinó por el rango de edad <strong>{applicableRange.rangeStart} - {applicableRange.rangeEnd} años</strong> del titular o familiar de mayor edad.
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsAgePricingModalOpen(true)}
                    className="text-blue-700 hover:underline font-medium whitespace-nowrap ml-2"
                  >
                    Ver todos los rangos
                  </button>
                </div>
              </div>
            )}
          </div>

          <Dialog open={isAgePricingModalOpen} onOpenChange={setIsAgePricingModalOpen}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Precios por Edad Aplicables
                </DialogTitle>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto pr-2">
                {ranges && ranges.length > 0 ? (
                  <div className="py-4">
                    <AgePricingRangeForm
                      ranges={ranges}
                      onChange={() => { }}
                      readOnly={true}
                    />
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground italic">
                    No se encontraron rangos de edad para este registro.
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Optional Notes */}
          {form ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Notas (Opcional)</label>
              <Textarea
                placeholder="Notas adicionales"
                className="resize-none"
                rows={3}
                {...form.register('notes')}
              />
              {form.formState.errors.notes && (
                <p className="text-sm text-red-500">{form.formState.errors.notes?.message as string}</p>
              )}
            </div>
          ) : (
            agreement.notes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Notas</label>
                <p className="text-sm bg-muted p-3 rounded-md">{agreement.notes}</p>
              </div>
            )
          )}

          {deathData && deathData.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {deathData.length > 1 ? 'Datos de los Fallecidos' : 'Datos del Fallecido'}
              </label>
              <div className="space-y-4 mt-3">
                {deathData.map((data, index) => (
                  <Card key={index} className="bg-muted/50">
                    <CardContent className="p-4">
                      <span className="font-medium text-md text-muted-foreground block mb-1">Datos Personales</span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Nombre:</span>
                          <span>{data.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Documento:</span>
                          <span>{data.docType}</span>
                          <span>{data.docNumber}</span>
                        </div>
                        {data.birthDate && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Nacimiento:</span>
                            <span>{format(new Date(data.birthDate), 'PPP', { locale: es })}</span>
                            {!data.deathDate && <span className="text-sm text-muted-foreground">({calculateAgeStr(data.birthDate)})</span>}
                          </div>
                        )}
                        {data.deathDate && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Fallecimiento:</span>
                            <span>{format(new Date(data.deathDate), 'PPP', { locale: es })}</span>
                            {data.birthDate && <span className="text-sm text-muted-foreground">({calculateAgeStr(data.birthDate, data.deathDate)})</span>}
                          </div>
                        )}
                        {data.cremationDate && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Cremación:</span>
                            <span>{format(new Date(data.cremationDate), 'PPP', { locale: es })}</span>
                          </div>
                        )}
                        {data.intermentDate && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Inhumación:</span>
                            <span>{format(new Date(data.intermentDate), 'PPP', { locale: es })}</span>
                          </div>
                        )}
                        {data.relationshipWithDeceased && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Parentesco del titular con el fallecido:</span>
                            <span>{formatDeathRelationship(data.relationshipWithDeceased)}</span>
                          </div>
                        )}
                        {(data.entryNumber || data.volume || data.year || data.leaf || data.office || data.mortuary || data.medicOfDeceased || data.deathCertificateAuthorization) && (
                          <div className="col-span-1 md:col-span-2 mt-2 pt-2 border-t">
                            <span className="font-medium text-md text-muted-foreground block mb-1">Datos de Registro</span>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
                              {data.entryNumber && <div><span className="font-semibold">Acta:</span> {data.entryNumber}</div>}
                              {data.volume && <div><span className="font-semibold">Tomo:</span> {data.volume}</div>}
                              {data.year && <div><span className="font-semibold">Año:</span> {data.year}</div>}
                              {data.leaf && <div><span className="font-semibold">Folio:</span> {data.leaf}</div>}
                              {data.office && <div><span className="font-semibold">Oficina:</span> {data.office}</div>}
                              {data.mortuary && <div><span className="font-semibold">Funeraria:</span> {data.mortuary}</div>}
                              {data.medicOfDeceased && <div className="col-span-2"><span className="font-semibold">Médico:</span> {data.medicOfDeceased}</div>}
                              {data.deathCertificateAuthorization && <div className="col-span-2"><span className="font-semibold">Autorización de sepultura:</span> {data.deathCertificateAuthorization}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {assuredPeople && assuredPeople.length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {assuredPeople.length > 1 ? 'Personas Aseguradas' : 'Persona Asegurada'}
              </label>
              <div className="space-y-4 mt-3">
                {assuredPeople.map((data, index) => (
                  <Card key={index} className="bg-muted/50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Nombre:</span>
                          <span>{data.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Documento:</span>
                          <span>{data.docType}</span>
                          <span>{data.docNumber}</span>
                        </div>
                        {data.dateOfBirth && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Nacimiento:</span>
                            <span>{format(new Date(data.dateOfBirth), 'PPP', { locale: es })}</span>
                            <span className="text-sm text-muted-foreground">({calculateAgeStr(new Date(data.dateOfBirth))})</span>
                          </div>
                        )}
                        {data.relationship && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">Parentesco:</span>
                            <span>{formatAssuredPersonRelationship(data.relationship)}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
